import React, { ReactElement } from 'react';
import {
  Box,
  Collapse,
  Divider,
  Flex,
  IconName,
  Pane,
  Pill,
  Tooltip,
  Typography,
} from '@fullstory/components';
import {
  EngagementMapsRankIndicator,
  EngagementMapsRankIndicatorProps,
} from '../EngagementMapsRankIndicator';
import { classNames } from '@fullstory/util/fmt';
import css from './index.module.scss';
import { Flags } from '@fullstory/client-api';
import { NamedElementRecord } from '@fullstory/proto/fs/services/search-settings';
import {
  FGACButton,
  getFGACButtonExplorerRoleProps,
  noPermissionMessage,
} from '@fullstory/library-ui';
import { Flag } from '@fullstory/proto/fs/shared/flags';
import {
  NamedElementModalSource,
  NamedElementTooltip,
  useNamedElementModal,
} from '@fullstory/named-elements-ui';
import { useFlags } from '@fullstory/react-hooks';
import { intl } from '@fullstory/intl';

export function createSearchActions(
  flags: Flags,
  onAddToSearch?: (rank: number) => void,
  onCreateSearch?: (rank: number) => void,
  onAddDefinedEvent?: (rank: number) => void,
): Array<EngagementMapsRankedCardActionRowInfo> {
  const result: Array<EngagementMapsRankedCardActionRowInfo> = [];
  if (onAddToSearch) {
    result.push({
      title: intl.formatMessage({ defaultMessage: 'Add to search', id: 'j13Jsp' }),
      action: onAddToSearch,
      icon: 'search-plus',
      canAccess: true,
    });
  }
  if (onCreateSearch) {
    result.push({
      title: intl.formatMessage({ defaultMessage: 'Create new search', id: 'DwQYwU' }),
      action: onCreateSearch,
      icon: 'search',
      canAccess: true,
    });
  }
  if (onAddDefinedEvent) {
    result.push({
      title: intl.formatMessage({ defaultMessage: 'Define event', id: '3GU9gH' }),
      action: onAddDefinedEvent,
      icon: 'defined-event',
      canAccess: flags.hasBit(Flag.PermModifyDefinedEvents) ?? true,
    });
  }
  return result;
}

export interface EngagementMapsRankedCardActionRowInfo {
  /**
   * The title of the action.
   */
  title: string;
  /**
   * The callback to perform when the user clicks on the action.
   */
  action: (rank: number) => void;
  /**
   * Icon accompanying the clickable action
   */
  icon?: IconName;
  /**
   * User permissions for FGAC
   */
  canAccess: boolean;
}

interface EngagementMapsRankedCardActionGroupProps {
  group: string;
  actions: Array<EngagementMapsRankedCardActionRowInfo>;
  rank: number;
}

/**
 * A helper method responsible for displaying actions in a group.
 */
const EngagementMapsRankedCardActionGroup: React.FC<EngagementMapsRankedCardActionGroupProps> = ({
  group,
  actions,
  rank,
}) => (
  <>
    <Box padding={[16, 0, 0, 16]} data-fs="engagement-maps-ranked-card-action-group">
      <Typography variant="heading6" color="neutrals-n7">
        {group}
      </Typography>
    </Box>
    {actions.map((info, i) => (
      <Box
        padding={[8, 0, 0, 16]}
        key={`engagement-maps-${group}-${rank}-${i}`}
        className={classNames({ [css.buttonContainer]: info.canAccess })}
      >
        <FGACButton
          testId="engagement-maps-ranked-card-action-row-button"
          variant="minimal"
          disabled={!info.canAccess}
          tooltip={intl.formatMessage(noPermissionMessage)}
          onClick={() => info.action(rank)}
          iconLeft={info.icon}
          {...getFGACButtonExplorerRoleProps(info.canAccess)}
        >
          {info.title}
        </FGACButton>
      </Box>
    ))}
  </>
);

export interface EngagementMapsRankedCardFactRowProps {
  /**
   * The main content of the fact.
   */
  title: string;
  /**
   * A subtitle explaining the fact.
   */
  subtitle: string;
  /**
   * A tooltip explaining the contents.
   */
  toolTipContent: ReactElement;
}

/**
 * A helper method responsible for displaying a single fact row.
 */
const EngagementMapsRankedCardFactRow: React.FC<EngagementMapsRankedCardFactRowProps> = ({
  title,
  subtitle,
  toolTipContent,
}) => (
  <Box
    data-component="EngagementMapsRankedCardFactRow"
    data-fs="engagement-maps-ranked-card-fact-row"
    padding={24}
  >
    <Tooltip placement="top" tooltipContent={toolTipContent} maxWidth={200}>
      <Flex column>
        <Box>
          <Typography variant="heading1" color="neutrals-n11">
            {title}
          </Typography>
        </Box>
        <Box>
          <Typography variant="heading6" color="neutrals-n7">
            {subtitle}
          </Typography>
        </Box>
      </Flex>
    </Tooltip>
  </Box>
);

export interface EngagementMapsRankedCardProps {
  /**
   * The rank indicator for the item. The active state of this will be determined by whether the card is expanded.
   */
  rankIndicator?: EngagementMapsRankIndicatorProps;
  /**
   * The selector of the ranked object.
   */
  selector: string;
  /**
   * The named elements matching the full selector.
   */
  namedElementRecords: NamedElementRecord[];
  /**
   * Whether or not the card is currently expanded.
   */
  expanded: boolean;
  /**
   * A callback to handle when the card is clicked on. Typically, the caller will expand the card.
   */
  onClick: (rank: number) => void;
  /**
   * An array of facts related to the object.
   */
  facts?: Array<EngagementMapsRankedCardFactRowProps>;
  /**
   * An array of search actions associated with the card.
   */
  searchActions?: Array<EngagementMapsRankedCardActionRowInfo>;
  /**
   * An array of element recording actions associated with the card.
   */
  recordingActions?: Array<EngagementMapsRankedCardActionRowInfo>;
}

/**
 * A details card used to display information about a list of ranked objects (e.g. Click Maps selectors).
 * This can also be used to display information about a list of one (e.g. Inspect Mode selector).
 */
export const EngagementMapsRankedCard: React.FC<EngagementMapsRankedCardProps> = ({
  rankIndicator,
  selector,
  namedElementRecords,
  expanded,
  onClick,
  facts = [],
  searchActions = [],
  recordingActions = [],
}) => {
  const rank = rankIndicator?.rank ?? 0;

  const flags = useFlags();
  const canAccessNamedElements = flags.hasBit(Flag.PermModifyNamedElements);
  const { setModalInput: setNamedElementModalInput, NamedElementModalComponent } =
    useNamedElementModal(NamedElementModalSource.inspect);

  const namedElementNames =
    namedElementRecords.length > 0 ? (
      <Box marginBottom={4}>
        {namedElementRecords.map(ner => (
          <NamedElementTooltip
            placement="left"
            key={ner.namedElement!.id}
            canAccess={canAccessNamedElements}
            namedElementRecord={ner}
            onEdit={() => setNamedElementModalInput(ner)}
          >
            <Pill key={ner.namedElement!.id} variant="primary">
              <Typography variant="body3" fontWeight={expanded ? 'semibold' : undefined}>
                {ner.namedElement!.name}
              </Typography>
            </Pill>
          </NamedElementTooltip>
        ))}
      </Box>
    ) : null;

  return (
    <Pane
      data-component="EngagementMapsRankedCard"
      data-fs="engagement-maps-ranked-card"
      data-rank={rank}
      data-expanded={expanded}
      backgroundColor="default"
      border="standard"
      borderRadius={6}
      paddingTop={16}
      paddingBottom={16}
      paddingLeft={8}
      paddingRight={8}
    >
      {rankIndicator ? (
        <Box className={classNames(css.clickable)} onClick={() => onClick(rank)}>
          <Box paddingBottom={expanded ? 8 : 0}>
            <Flex inline>
              <Box paddingLeft={8} paddingRight={8}>
                <EngagementMapsRankIndicator {...rankIndicator} active={expanded} />
              </Box>
              <Box>
                {namedElementNames}
                <Tooltip
                  placement="top"
                  showDelay={1000}
                  tooltipContent={
                    <Box>
                      <Typography
                        variant="body3"
                        color="neutrals-n5"
                        fontWeight={expanded ? 'semibold' : undefined}
                      >
                        {selector}
                      </Typography>
                    </Box>
                  }
                  maxWidth={200}
                >
                  <Box maxWidth={270}>
                    <Typography
                      tag="div"
                      ellipsize
                      variant="body3"
                      color="neutrals-n10"
                      fontWeight={expanded ? 'semibold' : undefined}
                    >
                      {selector}
                    </Typography>
                  </Box>
                </Tooltip>
              </Box>
            </Flex>
          </Box>
        </Box>
      ) : (
        namedElementNames
      )}
      <Collapse expanded={expanded}>
        {facts.map((props, i) => (
          <Box key={props.title}>
            <Box paddingBottom={i + 1 == facts.length ? 8 : 0}>
              <EngagementMapsRankedCardFactRow {...props} />
            </Box>
            {i + 1 !== facts.length && (
              <Flex>
                <Box width={30}>
                  <Divider variant="dark" />
                </Box>
              </Flex>
            )}
          </Box>
        ))}
        <>
          <Divider variant="medium" />
          {!!searchActions.length && (
            <EngagementMapsRankedCardActionGroup
              group="Search"
              actions={searchActions}
              rank={rank}
            />
          )}
          {!!recordingActions.length && (
            <EngagementMapsRankedCardActionGroup
              group={intl.formatMessage({
                defaultMessage: 'Element data capture rules',
                id: 'XkyhaM',
              })}
              actions={recordingActions}
              rank={rank}
            />
          )}
        </>
      </Collapse>
      {NamedElementModalComponent}
    </Pane>
  );
};
